import { History, Package, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { EmptyState } from '@/components/feedback/EmptyState'
import { Button } from '@/components/ui/Button'
import {
  Card,
  CardBody,
  CardHeader,
  CardSubtitle,
  CardTitle,
} from '@/components/ui/Card'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatNumber, formatTimestamp } from '@/lib/format'
import {
  toCargoPayload,
  toCostLinePayload,
  useCreateCargo,
  useCreateCostLine,
  useDeleteCargo,
  useDeleteCostLine,
  useUpdateCargo,
  useUpdateCostLine,
} from '@/features/quotes/api/useQuote'
import { errorMessage, issueText } from '@/features/quotes/quoteText'
import { CargoFormSheet } from '@/features/quotes/components/CargoFormSheet'
import { CostLineEditor } from '@/features/quotes/components/CostLineEditor'
import { CostLineFormSheet } from '@/features/quotes/components/CostLineFormSheet'
import {
  DealTermsFields,
  TermsReadout,
} from '@/features/quotes/components/QuoteHeaderExtras'
import { QuoteBlocksCard } from '@/features/quotes/components/QuoteBlocksCard'
import { QuoteNotesCard } from '@/features/quotes/components/QuoteNotesCard'
import { RouteOptionsCard } from '@/features/quotes/components/RouteOptionsCard'
import type { CargoPosition, CostLine, QuoteDetail } from '@/types/domain'

/* The quote's sections, as components rather than as page markup.
 *
 * These lived inside QuoteDetailPage until the stepped builder arrived and
 * needed the same cargo table, the same cost editor and the same terms panel
 * one step at a time. They are here — not duplicated there — because each one
 * owns MUTATIONS and a sheet: a second copy would be a second place where a
 * failed write decides whether to keep the draft, and the two would drift.
 *
 * Nothing about them knows which surface is hosting them. The wizard shows one
 * at a time behind a step rail; the detail page stacks them all. That is the
 * whole difference, and it belongs to the pages.
 */

/* One header total (FR-13): the figure that counts, and what it disagrees with.
 *
 * Three states, and the boring one is the common one. Unmeasured renders an
 * em dash rather than 0 — a load nobody has weighed does not weigh nothing, and
 * printing a zero is the invented number FR-12 exists to stop. A partial sum
 * renders with a marker: it is a REAL number that omits some positions, and a
 * header silently short by three unweighed crates books too small a truck. An
 * overridden figure names the sum beneath it, so the correction can be checked.
 *
 * Nothing here recalculates. The sum arrives computed from the server on every
 * read, which is what makes the absent recalculate control honest (UX-DR-5). */
function TotalFigure({
  effective,
  computed,
  overridden,
  partial,
  fractionDigits,
}: {
  effective?: string
  computed?: string
  overridden: boolean
  partial: boolean
  fractionDigits?: number
}) {
  const { t } = useTranslation()

  if (!effective) return <span className="text-text-muted">—</span>

  return (
    <span
      title={
        overridden && computed
          ? t('quotes.cargo.overriddenFrom', {
              value: formatNumber(computed, fractionDigits),
            })
          : partial
            ? t('quotes.cargo.partialTotal')
            : undefined
      }
    >
      {formatNumber(effective, fractionDigits)}
      {overridden ? (
        <span className="text-attention ml-1 font-normal">
          {t('quotes.cargo.overriddenMark')}
        </span>
      ) : partial ? (
        <span className="text-attention ml-1 font-normal">*</span>
      ) : null}
    </span>
  )
}

/* The sheet's target lives one level up, in the page, because the checklist
 * banner opens it too: "Заполнить груз" has to land on the position the banner
 * is complaining about, and a state owned privately here could not be reached
 * from up there. Everything else about the sheet stays this component's. */
export function CargoSection({
  record,
  target,
  setTarget,
}: {
  record: QuoteDetail
  target: CargoPosition | 'new' | null
  setTarget: (target: CargoPosition | 'new' | null) => void
}) {
  const { t } = useTranslation()
  const readOnly = record.status !== 'draft'
  const cargo = record.cargo ?? []
  const checklist = record.checklist ?? []

  const create = useCreateCargo(record.id)
  const update = useUpdateCargo(record.id)
  const remove = useDeleteCargo(record.id)
  const pending = create.isPending || update.isPending || remove.isPending
  // Whichever write last failed is the one to report. The sheet stays open on
  // a refusal with the draft intact, so the message belongs beside it.
  const failure = create.error ?? update.error ?? remove.error

  return (
    <Card>
      <CardHeader
        action={
          readOnly ? undefined : (
            <Button
              variant="secondary"
              size="sm"
              iconLeft={Plus}
              onClick={() => setTarget('new')}
            >
              {t('quotes.cargo.add')}
            </Button>
          )
        }
      >
        <CardTitle>{t('quotes.cargo.title')}</CardTitle>
      </CardHeader>

      {cargo.length === 0 ? (
        <EmptyState
          icon={Package}
          title={t('quotes.cargo.emptyTitle')}
          description={t('quotes.cargo.emptyDescription')}
        />
      ) : (
      <div className="asa-scroll overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="border-line border-b">
              <th scope="col" className="asa-eyebrow h-9 px-[18px]">{t('quotes.cargo.position')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3">{t('quotes.cargo.hsCode')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('quotes.cargo.packages')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('quotes.cargo.weight')}</th>
              <th scope="col" className="asa-eyebrow h-9 px-3 text-right">{t('quotes.cargo.volume')}</th>
              <th scope="col" className="asa-eyebrow h-9 w-10 px-[18px]" />
            </tr>
          </thead>
          <tbody>
            {cargo.map((position, index) => {
              // A position with an open checklist issue is what the trailing
              // flag column exists for. Healthy rows stay empty.
              const issues = checklist.filter(
                (issue) => issue.positionId === position.id,
              )

              return (
                <tr
                  key={position.id}
                  className={cn(
                    'border-line-hair h-[38px] border-b last:border-b-0',
                    !readOnly && 'hover:bg-surface-hover cursor-pointer transition-colors',
                  )}
                  onClick={readOnly ? undefined : () => setTarget(position)}
                  tabIndex={readOnly ? undefined : 0}
                  aria-label={readOnly ? undefined : t('quotes.cargo.edit')}
                  onKeyDown={
                    readOnly
                      ? undefined
                      : (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setTarget(position)
                          }
                        }
                  }
                >
                  <td className="px-[18px]">
                    <span className="flex items-center gap-2">
                      <span className="text-text-heading text-[13px]">
                        {index + 1}. {position.description}
                      </span>
                      {position.isChemical ? (
                        <Tag tone="attention">Chemical</Tag>
                      ) : null}
                    </span>
                  </td>
                  <td className="text-text-muted px-3 font-mono text-[12.5px]">
                    {position.hsCode ?? '—'}
                  </td>
                  <td className="asa-num text-text-body px-3 text-right text-[13px]">
                    {position.packages} {position.packageType}
                  </td>
                  <td className="asa-num text-text-body px-3 text-right text-[13px]">
                    {formatNumber(position.weightKg)}
                  </td>
                  <td
                    className={cn(
                      'asa-num px-3 text-right text-[13px]',
                      position.volumeCbm ? 'text-text-body' : 'text-attention',
                    )}
                  >
                    {position.volumeCbm
                      ? formatNumber(position.volumeCbm, 1)
                      : t('quotes.cargo.missing')}
                  </td>
                  <td className="px-[18px]">
                    {issues.length > 0 ? (
                      <span
                        className="bg-attention-soft text-attention asa-num flex size-5 items-center justify-center rounded-full text-[11px] font-semibold"
                        title={issues.map((issue) => issueText(issue, t)).join(' · ')}
                      >
                        {issues.length}
                        <span className="sr-only">
                          {t('quotes.checklist.blocked', { count: issues.length })}
                        </span>
                      </span>
                    ) : null}
                  </td>
                </tr>
              )
            })}
          </tbody>

          {/* The header totals (FR-13). Summed by the SERVER on every read, so
              there is nothing here to refresh and no "recalculate" control
              anywhere in the product (UX-DR-5). An overridden figure is marked
              as such and shows the sum it disagrees with, because an override
              nobody can check against the arithmetic has no provenance. */}
          {record.cargoTotals ? (
            <tfoot>
              {/* Tinted, not merely ruled. A hairline is what separates one
                  position from the next, so the same hairline under the last of
                  them said "another row" — and the totals row is a different
                  KIND of number: summed by the server (FR-13), not typed by
                  anybody. The tint is what makes it read as the foot of the
                  table rather than as position four. */}
              <tr className="border-line bg-neutral-25 border-t">
                <td
                  className="asa-eyebrow text-text-muted px-[18px] py-2"
                  colSpan={3}
                >
                  {t('quotes.cargo.totals')}
                </td>
                <td className="asa-num text-text-heading px-3 py-2 text-right text-[13px] font-semibold">
                  <TotalFigure
                    effective={record.cargoTotals.effectiveGrossWeightKg}
                    computed={record.cargoTotals.grossWeightKg}
                    overridden={record.cargoTotals.grossWeightOverridden}
                    partial={record.cargoTotals.weightPartial}
                  />
                </td>
                <td className="asa-num text-text-heading px-3 py-2 text-right text-[13px] font-semibold">
                  <TotalFigure
                    effective={record.cargoTotals.effectiveVolumeCbm}
                    computed={record.cargoTotals.volumeCbm}
                    overridden={record.cargoTotals.volumeOverridden}
                    partial={record.cargoTotals.volumePartial}
                    fractionDigits={1}
                  />
                </td>
                <td className="px-[18px]" />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
      )}

      {/* The two facts that have no column of their own: what the carrier
          bills, and whether the volumes above are the client's or our
          estimate (FR-12, FR-13). */}
      {record.cargoTotals &&
      (record.cargoTotals.effectiveChargeableWeightKg ||
        record.cargoTotals.finalPackingListReceived) ? (
        <div className="border-line-hair text-text-muted flex flex-wrap gap-x-5 gap-y-1 border-t px-[18px] py-2.5 text-[12.5px]">
          {record.cargoTotals.effectiveChargeableWeightKg ? (
            <span>
              {t('quotes.cargo.chargeableWeight')}:{' '}
              <span className="asa-num text-text-body">
                {formatNumber(record.cargoTotals.effectiveChargeableWeightKg)}
              </span>
            </span>
          ) : null}
          {record.cargoTotals.finalPackingListReceived ? (
            <span className="text-positive">
              {t('quotes.cargo.packingListReceived')}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* The sheet closes from the per-call onSuccess, never from onSubmit, so
          a refusal leaves it open with the draft intact rather than discarding
          what somebody typed — the failure this surface shipped with once. */}
      <CargoFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={target === 'new' || target === null ? undefined : target}
        pending={pending}
        error={failure ? errorMessage(failure, t) : undefined}
        onDelete={
          target === 'new' || target === null
            ? undefined
            : () => remove.mutate(target.id, { onSuccess: () => setTarget(null) })
        }
        onSubmit={(draft) => {
          const payload = toCargoPayload(draft)
          if (target === 'new') {
            create.mutate(payload, { onSuccess: () => setTarget(null) })
            return
          }
          if (target) {
            update.mutate(
              { cargoId: target.id, payload },
              { onSuccess: () => setTarget(null) },
            )
          }
        }}
      />
    </Card>
  )
}

/* The costing (Story 2.3, FR-1).
 *
 * Built like CargoSection and for the same reasons: the section owns the
 * mutations and the sheet, so a refusal can leave the sheet open with the draft
 * intact rather than discarding what somebody typed.
 *
 * The totals come from the server with the lines. Nothing here recomputes the
 * margin — see CostLineEditor for why.
 */
export function CostLineSection({
  record,
  target,
  setTarget,
}: {
  record: QuoteDetail
  target: CostLine | 'new' | null
  setTarget: (target: CostLine | 'new' | null) => void
}) {
  const { t } = useTranslation()
  const readOnly = record.status !== 'draft'

  const create = useCreateCostLine(record.id)
  const update = useUpdateCostLine(record.id)
  const remove = useDeleteCostLine(record.id)
  const pending = create.isPending || update.isPending || remove.isPending
  // Whichever write last failed is the one to report, as on the cargo section.
  const failure = create.error ?? update.error ?? remove.error

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotes.costLines.title')}</CardTitle>
      </CardHeader>

      {/* A refusal of an INLINE save has nowhere else to go: the sheet is what
          normally carries `failure`, and it is closed. Rendered only when the
          sheet is shut so one error never appears in two places at once. */}
      {failure && target === null ? (
        <p
          role="alert"
          className="bg-negative-soft text-negative mx-[18px] mb-3 rounded-md px-3 py-2 text-[12.5px] leading-snug"
        >
          {errorMessage(failure, t)}
        </p>
      ) : null}

      <CostLineEditor
        lines={record.costLines ?? []}
        totals={record.costTotals}
        readOnly={readOnly}
        onEdit={setTarget}
        pending={pending}
        /* Adding, editing and removing are all inline on desktop; the sheet
           remains the phone's path for all three. The row clears or closes
           itself through `onSaved` only on success, so a refusal leaves what
           was typed on screen — the same bargain the sheet makes, and the
           reason the error above is not scoped to the sheet. */
        onAddInline={(draft, onSaved) =>
          create.mutate(toCostLinePayload(draft), { onSuccess: onSaved })
        }
        onEditInline={(line, draft, onSaved) =>
          update.mutate(
            { lineId: line.id, payload: toCostLinePayload(draft) },
            { onSuccess: onSaved },
          )
        }
        /* No onSuccess: the row that armed this delete is gone from the next
           render of the list, so there is nothing left to close. */
        onDeleteInline={(line) => remove.mutate(line.id)}
      />

      {/* The sheet closes from the per-call onSuccess, never from onSubmit, so
          a refusal leaves it open with the draft intact. */}
      <CostLineFormSheet
        open={target !== null}
        onClose={() => setTarget(null)}
        record={target === 'new' || target === null ? undefined : target}
        pending={pending}
        error={failure ? errorMessage(failure, t) : undefined}
        onDelete={
          target === 'new' || target === null
            ? undefined
            : () => remove.mutate(target.id, { onSuccess: () => setTarget(null) })
        }
        onSubmit={(draft) => {
          const payload = toCostLinePayload(draft)
          if (target === 'new') {
            create.mutate(payload, { onSuccess: () => setTarget(null) })
            return
          }
          if (target) {
            update.mutate(
              { lineId: target.id, payload },
              { onSuccess: () => setTarget(null) },
            )
          }
        }}
      />
    </Card>
  )
}

/* Route options moved to their own component with Story 2.4, where the
 * mutations and the FR-3 confirm flow live. The page only places it. */
export function RouteOptionsSection({ record }: { record: QuoteDetail }) {
  return <RouteOptionsCard record={record} />
}

/* The deal terms and the three blocks the offer prints (FR-11, FR-59, FR-60).
 *
 * Three cards. The deal terms and the notes share the top row — one is four
 * short answers, the other four sentences, and both fit a half — and the
 * included/not-included PAIR runs full width beneath them, because it is two
 * lists whose whole use is being read against each other.
 *
 * `editable` is the HOST's answer to "does this screen already ask these
 * questions somewhere else". It exists because these cards were hosted twice —
 * by the detail page and by a three-step builder whose pricing step asked every
 * one of the same fields one card up. Two editors for one field a hand's width
 * apart, and the first disagreement between them is a term somebody thought
 * they had changed.
 *
 * The builder is gone and the detail page passes `editable`, so today there is
 * one caller and one answer. The prop stays because the question it asks is the
 * one that will come up again the moment a second surface wants these sections:
 * `QuoteFormSheet` asks the same four fields, and a host that opens it must not
 * also open these.
 *
 * It reaches the TERMS card alone: the two block cards hold no header fields,
 * and their lists are editable on any draft from either host.
 *
 * The SERVER still decides. `record.editable === false` — strict, so a response
 * that never computed the field is not mistaken for a refusal, exactly as the
 * page-header Edit button reads it — puts the cards back to the read-out. A sent
 * quote's terms are what the client was told, and the way to change them is a
 * new revision, not a form.
 */
export function TermsSection({
  record,
  editable = false,
}: {
  record: QuoteDetail
  editable?: boolean
}) {
  const { t } = useTranslation()

  const amendable = editable && record.editable !== false

  return (
    <div className="flex flex-col gap-3.5">
      <div className="grid gap-3.5 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('quotes.terms.title')}</CardTitle>
            {amendable ? (
              <CardSubtitle>{t('quotes.terms.editHint')}</CardSubtitle>
            ) : editable ? (
              /* Said out loud rather than left as a card that simply does not
                 respond. A reader who came here to fix an Incoterm needs to know
                 the window is shut and what opens it. */
              <CardSubtitle>{t('quotes.revisions.notEditable')}</CardSubtitle>
            ) : null}
          </CardHeader>
          <CardBody className="pt-2">
            {amendable ? (
              <DealTermsFields record={record} />
            ) : (
              /* The three a client compares between offers. The demurrage
                 wording and the delivery description are on the notes card,
                 where they read as the prose they are.

                 The labels come from the catalogue, not from this file. They
                 were seven hardcoded English strings — 'Incoterm', 'Payment
                 terms', 'Valid until' — printed under a Russian heading on a
                 Russian screen, which is the one thing AD-27 asks this layer
                 not to do. Every key already existed; nothing was translated
                 for this, it was simply never wired. */
              <TermsReadout
                rows={[
                  [t('quotes.form.incoterm'), record.incoterm],
                  [t('quotes.form.paymentTerms'), record.paymentTerms],
                  [t('quotes.columns.validUntil'), formatDate(record.validUntil)],
                ]}
              />
            )}
          </CardBody>
        </Card>

        {/* The notes, beside the terms they qualify. A list of four sentences
            with no fields in it, which is why it fits in half a row where the
            included/excluded PAIR does not: that one is two lists read against
            each other, and side by side is the only way to read them. */}
        <QuoteNotesCard record={record} />
      </div>

      {/* Full width, because it is two lists rather than one. Read from and
          written to /quotes/:id/blocks.

          This used to render a fixture-shaped `notIncluded` array and a single
          `disclaimer` string that no server ever sent — a read-only echo of
          mock data, beside an offer composer that was already printing the real
          rows. Nothing on either surface reached the endpoints, so the blocks
          on every issued offer were empty by construction. */}
      <QuoteBlocksCard record={record} />
    </div>
  )
}

export function RevisionsSection({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()
  const revisions = record.revisions ?? []

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotes.revisions.title')}</CardTitle>
      </CardHeader>
      {revisions.length === 0 ? (
        <EmptyState
          icon={History}
          title={t('quotes.revisions.emptyTitle')}
          description={t('quotes.revisions.emptyDescription')}
        />
      ) : (
      <ul className="flex flex-col px-[18px] pb-3">
        {revisions.map((revision, index) => (
          <li
            key={revision.id}
            className={cn(
              'flex items-center gap-3 py-2.5',
              index !== revisions.length - 1 && 'border-line-hair border-b',
            )}
          >
            {/* Issued revisions are immutable snapshots, never deltas, and the
                history stays reachable from every later state including the
                case (AD-15, FR-7). */}
            <span className="text-brand w-12 shrink-0 text-[13px] font-semibold">
              {revision.label}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-text-heading truncate text-[13px]">
                {revision.note ?? 'Issued'}
              </span>
              <span className="text-text-muted text-[12px] font-light">
                {revision.issuedByName} · {formatTimestamp(revision.issuedAt)}
              </span>
            </span>
            <span className="flex-1" />
            <span className="asa-num text-text-heading text-[13px]">
              {formatMoney(revision.sell.amount, revision.sell.currency)}
            </span>
          </li>
        ))}
      </ul>
      )}
    </Card>
  )
}
